import { Typography } from '@euler/app/components/typography';
import { kApdmCampaignStatusLabels } from '@euler/functions/apdm/campaignLabels';
import { apdmSendingTimeText } from '@euler/functions/apdm/campaignSending';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ApdmCampaignMonitor, ApdmCampaignStatus } from '@euler/model/apdm';
import dayjs from 'dayjs';
import { LinearGradient } from 'expo-linear-gradient';
import {
  Ban,
  Eye,
  EyeOff,
  LucideIcon,
  MessageCircleMore,
  Star,
} from 'lucide-react-native';
import { memo } from 'react';
import { View } from 'react-native';
import { aiColors, tint } from '../theme';

/** A campaign's state as a dot: green while it sends. */
export function campaignStatusColor(status: ApdmCampaignStatus): string {
  switch (status) {
    case ApdmCampaignStatus.Reaching:
      return aiColors.green;
    case ApdmCampaignStatus.Scheduled:
      return aiColors.primary;
    case ApdmCampaignStatus.Paused:
      return aiColors.amber;
    case ApdmCampaignStatus.Reached:
      return aiColors.violet;
    default:
      return aiColors.mutedForeground;
  }
}

export const CampaignStatusDot = memo(
  ({ status }: { status: ApdmCampaignStatus }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View
        accessibilityLabel={kApdmCampaignStatusLabels[status]}
        style={{
          width: rpx(6),
          height: rpx(6),
          borderRadius: rpx(3),
          backgroundColor: campaignStatusColor(status),
        }}
      />
    );
  },
);

/** One line on where the sending stands. */
function statusLine(campaign: ApdmCampaignMonitor): string {
  const next = campaign.nextSendAt ? dayjs(campaign.nextSendAt) : null;
  switch (campaign.status) {
    case ApdmCampaignStatus.Paused:
      return '已暂停，继续后接着发送';
    case ApdmCampaignStatus.Reached:
      return '已全部发出，正在等客户回复';
    case ApdmCampaignStatus.Ended:
      return '活动已结束';
    default:
      if (!campaign.liveSendingAccounts) return '等待发送账号上线';
      if (!next) return '活动结束前已没有发送时段';
      if (next.isAfter(dayjs()))
        return campaign.status === ApdmCampaignStatus.Scheduled
          ? `将于${apdmSendingTimeText(next)}开始发送`
          : `${apdmSendingTimeText(next)}继续发送`;
      return campaign.queued
        ? `正在发送，${campaign.queued} 条消息排队中`
        : '每日时段内发送中';
  }
}

const kWhite = '#FFFFFF';

function Tile({
  icon: Icon,
  value,
  label,
  highlight,
}: {
  icon: LucideIcon;
  value: number;
  label: string;
  highlight?: boolean;
}) {
  const { rpx } = useResponsiveContext();
  const color = highlight ? aiColors.accentForeground : kWhite;
  return (
    <View
      style={{
        flex: 1,
        alignItems: 'center',
        gap: rpx(2),
        paddingVertical: rpx(8),
        borderRadius: rpx(12),
        backgroundColor: highlight ? aiColors.accent : tint(kWhite, 0.15),
      }}
    >
      <Icon size={16} color={color} strokeWidth={1.8} />
      <Typography.Text bold size={rpx(14)} color={color}>
        {value.toLocaleString('zh-CN')}
      </Typography.Text>
      <Typography.Text
        size={rpx(10)}
        color={highlight ? color : tint(kWhite, 0.8)}
      >
        {label}
      </Typography.Text>
    </View>
  );
}

/**
 * The campaign this conversation launched, live, at the top of the chat:
 * how far the sending got, what holds it up, and what it brought so far.
 */
export const AiCampaignMonitor = memo(
  ({ campaign }: { campaign: ApdmCampaignMonitor }) => {
    const { rpx } = useResponsiveContext();
    const share = campaign.audience
      ? Math.min(1, campaign.attempted / campaign.audience)
      : 0;
    const percent = Math.round(share * 100);
    return (
      <LinearGradient
        colors={[aiColors.primary, aiColors.violet]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={{
          marginTop: rpx(12),
          padding: rpx(16),
          borderRadius: rpx(22),
          overflow: 'hidden',
        }}
      >
        <View
          accessibilityLabel={`已发送 ${campaign.attempted}/${campaign.audience}`}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <View style={{ gap: rpx(4) }}>
            <Typography.Text size={rpx(11)} color={tint(kWhite, 0.8)}>
              已发送
            </Typography.Text>
            <Typography.Text bold size={rpx(28)} color={kWhite}>
              {campaign.attempted.toLocaleString('zh-CN')}
              <Typography.Text medium size={rpx(14)} color={tint(kWhite, 0.8)}>
                {' '}
                / {campaign.audience.toLocaleString('zh-CN')}
              </Typography.Text>
            </Typography.Text>
          </View>
          <View
            style={{
              width: rpx(56),
              height: rpx(56),
              borderRadius: rpx(28),
              alignItems: 'center',
              justifyContent: 'center',
              borderWidth: 1,
              borderColor: tint(kWhite, 0.3),
              backgroundColor: tint(kWhite, 0.15),
            }}
          >
            <Typography.Text bold size={rpx(14)} color={kWhite}>
              {percent}%
            </Typography.Text>
          </View>
        </View>
        <View
          style={{
            marginTop: rpx(16),
            height: rpx(10),
            borderRadius: rpx(5),
            backgroundColor: tint(kWhite, 0.2),
            overflow: 'hidden',
          }}
        >
          <View
            style={{
              width: `${share * 100}%`,
              height: '100%',
              borderRadius: rpx(5),
              backgroundColor: kWhite,
            }}
          />
        </View>
        <Typography.Text
          size={rpx(11)}
          color={tint(kWhite, 0.8)}
          style={{ marginTop: rpx(8) }}
        >
          {statusLine(campaign)}
        </Typography.Text>
        <View style={{ flexDirection: 'row', gap: rpx(6), marginTop: rpx(16) }}>
          <Tile icon={Eye} value={campaign.reached} label="已触达" />
          <Tile icon={EyeOff} value={campaign.unreachable} label="未触达" />
          <Tile icon={Ban} value={campaign.declined} label="拒收" />
          <Tile
            icon={MessageCircleMore}
            value={campaign.replied}
            label="回复"
          />
          <Tile icon={Star} value={campaign.leads} label="线索" highlight />
        </View>
      </LinearGradient>
    );
  },
);
